/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React from 'react';
import {useRouter} from 'next/router';
import PropTypes from 'prop-types';
import _ from 'lodash';

import {useAppContext, useAnalytics} from '../../hooks';
import {BASE_URL} from '../../constants/config';
import Button, {ButtonSizes} from '../../components/ButtonNew';
import {KlarnaIcon, Checkmark} from '../../components/svg';

const CoachingProducts = () => ([
  {
    name: 'Mock Interview (5-pack)',
    sku: 'COACHING_SESSION_5_PACK',
    quantity: 5,
    description: 'Five 1-hour mock interviews including detailed written feedback. Save 20% when you buy this bundle.',
    category: 'Mock Interviews',
    ctaText: 'Buy now',
  },
  {
    name: 'Mock Interview (3-pack)',
    sku: 'COACHING_SESSION_3_PACK',
    quantity: 3,
    description: 'Three 1-hour mock interviews including detailed written feedback. Save 10% when you buy this bundle.',
    category: 'Mock Interviews',
    ctaText: 'Buy now',
  },
  {
    name: 'Mock Interview',
    sku: 'COACHING_SESSION',
    quantity: 1,
    description: '1-hour mock interview including detailed written feedback.',
    category: 'Mock Interviews',
    ctaText: 'Buy now',
  },
  {
    name: 'Ace the Interview Program',
    sku: 'ACE_INTERVIEW_PROGRAM',
    quantity: 1,
    description: 'Our most comprehensive package with structured coaching, mock interviews, and access to all membership features.',
    category: 'Mock Interviews',
    isProgram: true,
    ctaText: 'Enroll now',
    features: [
      '1-hour readiness assessment',
      'Five 1-hour prep sessions or mock interviews',
      '1-year access to all Exponent courses and community',
      '20% off future coaching sessions',
    ],
  },
  {
    name: 'Applying to Jobs Program',
    sku: 'APPLYING_JOBS_PROGRAM',
    quantity: 1,
    description: 'Get invited to interview at your target companies. Craft your unique profile and leverage your strengths by identifying the right roles to apply for.',
    category: 'Applying to jobs',
    isProgram: true,
    ctaText: 'Enroll now',
    features: [
      '1-hour onboarding & goal setting',
      'Resume & LinkedIn review',
      'Two 1-hour coaching sessions',
      'Access to all Exponent courses',
      '20% off future coaching sessions',
    ],
  },
  {
    name: 'Career Coaching Session (3-pack)',
    sku: 'COACHING_SESSION_3_PACK',
    quantity: 3,
    description: 'Three 1-hour coaching sessions customized to your career goals. Save 10% with this bundle.',
    category: 'Career Coaching',
    ctaText: 'Buy now',
  },
  {
    name: 'Career Coaching Session',
    sku: 'COACHING_SESSION',
    quantity: 1,
    description: '1-hour coaching session customized to your career goals.',
    category: 'Career Coaching',
    ctaText: 'Buy now',
  },
  {
    name: 'Career Coaching Program',
    sku: 'CAREER_COACHING_PROGRAM',
    quantity: 1,
    description: 'Work with a career coach to develop a results-oriented plan to map out your goals and own your trajectory.',
    category: 'Career Coaching',
    isProgram: true,
    ctaText: 'Enroll now',
    features: [
      '1-hour career navigation & goal-setting',
      'Three 1-hour coaching sessions',
      '20% off future coaching sessions',
    ],
  },
  {
    name: 'Full-service Negotiation Coaching',
    sku: 'NEGOTIATION_PROGRAM',
    quantity: 1,
    description: 'Work with an expert negotiator involving as many back-and-forth emails and calls as you need. Increase your offer—or your money back.',
    category: 'Negotiation',
    isProgram: true,
    ctaText: 'Buy now',
    features: [
      'Personalized offer evaluation',
      'Unlimited time and meeting sessions',
      'Full refund if no increased offer',
      'Powered by private industry knowledge of salary data',
      'Proven negotiation strategies',
    ],
  },
  {
    name: '1-hour Negotiation Coaching',
    sku: 'COACHING_SESSION',
    quantity: 1,
    description: '1-hour session to review your offers and develop a strategy to maximize your compensation.',
    category: 'Negotiation',
    ctaText: 'Buy now',
  },
  {
    name: 'Communication Coaching',
    sku: 'COACHING_SESSION',
    quantity: 1,
    description: '1-hour session to discuss communication strategies, evaluate areas of improvement, and practice your communication.',
    category: 'Communication Skills',
    ctaText: 'Buy now',
  },
  {
    name: 'Mock Presentation',
    sku: 'COACHING_SESSION',
    quantity: 1,
    description: '1-hour session to practice giving a presentation and getting specific, concrete feedback.',
    category: 'Communication Skills',
    ctaText: 'Buy now',
  },
]);

/** Given a coach, determine what options are relevant to their services */
const getCoachingProducts = (coach) => CoachingProducts()
  .filter((product) => coach?.Services?.includes(product.category));

const CoachingServiceList = ({coach, hasCredits}) => {
  const router = useRouter();
  const {
    catalog, onCheckout, currentUser, features,
  } = useAppContext();
  const {tracker} = useAnalytics();
  const {COACHING_SESSION} = catalog;
  const cancelUrl = `${BASE_URL}/coach/${router.query.coach}?payment_canceled=true`;
  const isEligibleForBNPL = currentUser?.data?.country === 'US';

  const discounted = Boolean(COACHING_SESSION?.discountedPriceString);

  const getOriginalPrice = (product) => catalog[product?.sku]?.priceString;

  const getPriceAfterDiscount = (product) => catalog[product?.sku]?.discountedPriceString
  || catalog[product?.sku]?.priceString;

  const onClickBook = (product) => {
    tracker.coachClickBook({
      id: coach.slug,
      coach: coach.slug,
      role: coach.Role,
      companies: coach.Companies,
      source: 'profile',
      product: product.sku,
      quantity: product.quantity,
      selectedGoals: [product.category],
    });

    const products = [
      {
        quantity: product.quantity,
        coach: coach.slug,
        id: product.sku,
        goals: [product.category],
      },
    ];

    onCheckout(products, currentUser, {cancelUrl});
  };

  let coachingProducts = getCoachingProducts(coach);

  if (features?.programs_coach_profile !== 'on') {
    coachingProducts = coachingProducts.filter((product) => !product.isProgram);
  }

  return (
    <div className="w-full flex flex-col">
      <div className="mb-4 w-full flex justify-between items-center">
        <span className="text-base md:text-lg font-semibold">
          {hasCredits ? 'Purchase additional sessions' : 'Select your coaching plan'}
        </span>
        {isEligibleForBNPL && (
          <div className="hidden md:flex items-center justify-center font-medium text-xs text-gray-500 ml-2 md:block">
            Pay over time with
            <KlarnaIcon className="w-12 ml-1.5" />
          </div>
        )}
      </div>
      <div className="">
        {discounted && (
          <div className="flex w-full mb-4 p-4 border border-indigo-200 bg-indigo-50 rounded-lg font-medium text-sm text-indigo-600">
            Discount applied automatically!
          </div>
        )}
        <div className="space-y-4">
          {coachingProducts.map((product) => (
            <div
              className="flex flex-col md:flex-row justify-between gap-4 px-5 py-4 rounded-lg shadow-sm hover:shadow bg-white border border-gray-200 cursor-pointer transition duration-50"
              onClick={() => onClickBook(product)}
            >
              <div>
                {product.isProgram && (
                  <span className="inline-block px-2 py-0.5 -ml-0.5 mb-1.5 bg-indigo-50 text-indigo-600 text-xs font-medium rounded-full">
                    Program
                  </span>
                )}
                <h3 className="mb-1.5 text-lg md:text-xl font-semibold text-black">{product.name}</h3>
                <p className="text-sm md:text-base font-regular text-gray-600">{product.description}</p>
                {product.features && (
                  <div className="mt-2 mb-4 flex flex-col justify-center space-y-1">
                    {product.features.map((feature) => (
                      <span className="flex items-start font-medium text-xs md:text-sm text-gray-600">
                        <Checkmark className="flex-shrink-0 mt-0.5 mr-2" />
                        {feature}
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-2 flex items-end">
                  {(discounted && !product.isProgram) && (
                    <span className="text-base mr-2 text-green-500 line-through">
                      {getOriginalPrice(product)}
                    </span>
                  )}
                  <span className="text-base text-gray-600">
                    {getPriceAfterDiscount(product)}
                  </span>
                </div>
              </div>
              <div className="">
                <Button size={ButtonSizes.LARGE} className="w-full whitespace-nowrap" onClick={(e) => { e.stopPropagation(); onClickBook(product); }}>
                  {product.ctaText}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

CoachingServiceList.propTypes = {
  coach: PropTypes.shape().isRequired,
};

export default CoachingServiceList;
